import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import CategoriesAdmin from '../components/admin/CategoriesAdmin';
import ProductsAdmin from '../components/admin/ProductsAdmin';
import ContactsAdmin from '../components/admin/ContactsAdmin';
import OrdersAdmin from '../components/admin/OrdersAdmin';
import CarsAdmin from '../components/admin/CarsAdmin';
import VinRequestsAdmin from '../components/admin/VinRequestsAdmin';
import ReviewsAdmin from '../components/admin/ReviewsAdmin';
import { BACKEND_URL } from '../config';
import '../css/AdminPage.css';

// Ключ для зберігання активної секції в localStorage
const LOCAL_STORAGE_KEY_SECTION = 'adminActiveSection';

// Визначаємо можливі розділи адмінки
const ADMIN_SECTIONS = {
    CATEGORIES: 'categories',
    PRODUCTS: 'products',
    CONTACTS: 'contacts',
    ORDERS: 'orders',
    CARS: 'cars',
    VIN_REQUESTS: 'vin_requests',
    REVIEWS: 'reviews',
};

const AdminPage = () => {
    const navigate = useNavigate();
    // Стан для зберігання поточного активного розділу
    // При ініціалізації читаємо з localStorage або ставимо дефолт
    const [activeSection, setActiveSection] = useState(() => {
        const savedSection = localStorage.getItem(LOCAL_STORAGE_KEY_SECTION);
        // Перевіряємо, чи збережене значення є одним із допустимих
        return Object.values(ADMIN_SECTIONS).includes(savedSection) 
               ? savedSection 
               : ADMIN_SECTIONS.CATEGORIES; 
    });

    // Зберігаємо активну секцію в localStorage при зміні
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY_SECTION, activeSection);
    }, [activeSection]);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem(LOCAL_STORAGE_KEY_SECTION);
        navigate('/room-for-big-guys-qmttydsra-53231998/login', { replace: true });
    };

    // Sitemap generation
    const [sitemapStatus, setSitemapStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
    const [sitemapMessage, setSitemapMessage] = useState('');

    // Feed generation
    const [feedStatus, setFeedStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
    const [feedMessage, setFeedMessage] = useState('');

    // Reviews CSV import
    const [importStatus, setImportStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
    const [importMessage, setImportMessage] = useState('');
    const csvInputRef = React.useRef(null);

    const handleImportCsv = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImportStatus('loading');
        setImportMessage('');
        try {
            const token = localStorage.getItem('authToken');
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${BACKEND_URL}/reviews/admin/import`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            const text = await response.text();
            if (!response.ok) throw new Error(text || `HTTP ${response.status}`);
            setImportStatus('ok');
            setImportMessage(text);
            setTimeout(() => setImportStatus(null), 5000);
        } catch (err) {
            setImportStatus('error');
            setImportMessage(`Помилка: ${err.message}`);
        } finally {
            e.target.value = '';
        }
    };

    // Slug regeneration
    const [slugStatus, setSlugStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
    const [slugMessage, setSlugMessage] = useState('');

    const handleRegenerateSlugs = async () => {
        setSlugStatus('loading');
        setSlugMessage('');
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/products/admin/regenerate-slugs`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `HTTP ${response.status}`);
            }
            setSlugStatus('ok');
            setSlugMessage('Slug-и успішно перегенеровано');
            setTimeout(() => setSlugStatus(null), 4000);
        } catch (err) {
            setSlugStatus('error');
            setSlugMessage(`Помилка: ${err.message}`);
        }
    };

    const handleGenerateSitemap = async () => {        setSitemapStatus('loading');
        setSitemapMessage('');
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/api/sitemap/generate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `HTTP ${response.status}`);
            }
            setSitemapStatus('ok');
            setSitemapMessage('Sitemap успішно згенеровано');
            setTimeout(() => setSitemapStatus(null), 4000);
        } catch (err) {
            setSitemapStatus('error');
            setSitemapMessage(`Помилка: ${err.message}`);
        }
    };

    const handleGenerateFeed = async () => {
        setFeedStatus('loading');
        setFeedMessage('');
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/api/feed/generate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `HTTP ${response.status}`);
            }
            setFeedStatus('ok');
            setFeedMessage('Фід успішно згенеровано');
            setTimeout(() => setFeedStatus(null), 4000);
        } catch (err) {
            setFeedStatus('error');
            setFeedMessage(`Помилка: ${err.message}`);
        }
    };

    // Функція для рендерингу активного компонента
    const renderActiveSection = () => {
        switch (activeSection) {
            case ADMIN_SECTIONS.PRODUCTS:
                return <ProductsAdmin />;
            case ADMIN_SECTIONS.CONTACTS:
                return <ContactsAdmin />;
            case ADMIN_SECTIONS.CATEGORIES:
                return <CategoriesAdmin />;
            case ADMIN_SECTIONS.ORDERS:
                return <OrdersAdmin />;
            case ADMIN_SECTIONS.CARS:
                return <CarsAdmin />;
            case ADMIN_SECTIONS.VIN_REQUESTS:
                return <VinRequestsAdmin />;
            case ADMIN_SECTIONS.REVIEWS:
                return <ReviewsAdmin />;
            default:
                return <CategoriesAdmin />;
        }
    };

    return (
        <Layout>
            <SEO
                title="Панель Адміністратора"
                description="Адміністративна панель UnionAuto"
                noindex={true}
            />
            <div className="admin-page-container">
                <div className="admin-header">
                    <h1>Панель Адміністратора</h1>
                    <div className="admin-header-actions">
                        <div className="sitemap-action">
                            <input
                                ref={csvInputRef}
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={handleImportCsv}
                            />
                            <button
                                onClick={() => csvInputRef.current?.click()}
                                disabled={importStatus === 'loading'}
                                className="sitemap-button"
                                title="Імпортувати відгуки з CSV файлу"
                            >
                                {importStatus === 'loading' ? '⏳ Імпорт...' : '⭐ Імпорт відгуків'}
                            </button>
                            {importMessage && (
                                <span className={`sitemap-message sitemap-message--${importStatus}`}>
                                    {importMessage}
                                </span>
                            )}
                        </div>
                        <div className="sitemap-action">
                            <button
                                onClick={handleRegenerateSlugs}
                                disabled={slugStatus === 'loading'}
                                className="sitemap-button"
                                title="Перегенерувати slug-и для всіх товарів (виправляє URL брендів)"
                            >
                                {slugStatus === 'loading' ? '⏳ Генерація...' : '🔗 Регенерувати slug-и'}
                            </button>
                            {slugMessage && (
                                <span className={`sitemap-message sitemap-message--${slugStatus}`}>
                                    {slugMessage}
                                </span>
                            )}
                        </div>
                        <div className="sitemap-action">
                            <button
                                onClick={handleGenerateSitemap}
                                disabled={sitemapStatus === 'loading'}
                                className="sitemap-button"
                                title="Згенерувати sitemap.xml з усіма товарами, категоріями та марками"
                            >
                                {sitemapStatus === 'loading' ? '⏳ Генерація...' : '🗺 Sitemap'}
                            </button>
                            {sitemapMessage && (
                                <span className={`sitemap-message sitemap-message--${sitemapStatus}`}>
                                    {sitemapMessage}
                                </span>
                            )}
                        </div>
                        <div className="sitemap-action">
                            <button
                                onClick={handleGenerateFeed}
                                disabled={feedStatus === 'loading'}
                                className="sitemap-button"
                                title="Згенерувати product-feed.xml для Google Merchant / Facebook"
                            >
                                {feedStatus === 'loading' ? '⏳ Генерація...' : '📦 Фід товарів'}
                            </button>
                            {feedMessage && (
                                <span className={`sitemap-message sitemap-message--${feedStatus}`}>
                                    {feedMessage}
                                </span>
                            )}
                        </div>
                        <button onClick={handleLogout} className="logout-button">
                            Вийти
                        </button>
                    </div>
                </div>

                <div className="admin-content-wrapper">
                    {/* Бічне меню */}
                    <nav className="admin-sidebar">
                        <ul>
                            <li className={activeSection === ADMIN_SECTIONS.CATEGORIES ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.CATEGORIES)}>
                                    Категорії
                                </button>
                            </li>
                            <li className={activeSection === ADMIN_SECTIONS.PRODUCTS ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.PRODUCTS)}>
                                    Товари
                                </button>
                            </li>
                            <li className={activeSection === ADMIN_SECTIONS.CARS ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.CARS)}>
                                    Марки та моделі
                                </button>
                            </li>
                            <li className={activeSection === ADMIN_SECTIONS.CONTACTS ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.CONTACTS)}>
                                    Контакти
                                </button>
                            </li>
                            <li className={activeSection === ADMIN_SECTIONS.ORDERS ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.ORDERS)}>
                                    Активні замовлення
                                </button>
                            </li>
                            <li className={activeSection === ADMIN_SECTIONS.VIN_REQUESTS ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.VIN_REQUESTS)}>
                                    VIN-заявки
                                </button>
                            </li>
                            <li className={activeSection === ADMIN_SECTIONS.REVIEWS ? 'active' : ''}>
                                <button onClick={() => setActiveSection(ADMIN_SECTIONS.REVIEWS)}>
                                    Відгуки
                                </button>
                            </li>
                        </ul>
                    </nav>

                    {/* Основний контент адмінки */}
                    <main className="admin-main-content">
                        {renderActiveSection()} 
                    </main>
                </div>
            </div>
        </Layout>
    );
};

export default AdminPage; 